import React, { useState, useEffect, useCallback } from 'react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const jsonFetch = (url, options = {}) => fetch(url, {
  credentials: 'include',
  headers: { 'Content-Type': 'application/json' },
  ...options,
});

export const ClinicLabsPanel = ({ canManage }) => {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(null);
  const [newLab, setNewLab] = useState('');
  const [newProduct, setNewProduct] = useState({ name: '', stock_count: 0, unit_label: 'UNI' });
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await jsonFetch(`${API}/clinic/labs`);
      if (res.ok) setData(await res.json());
    } catch {
      setData({ labs: [], total_labs: 0, total_products: 0 });
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const run = async (fn, okText) => {
    setBusy(true);
    setMessage(null);
    try {
      const res = await fn();
      const body = await res.json();
      if (!res.ok) throw new Error(typeof body.detail === 'string' ? body.detail : 'No se pudo completar la acción');
      setMessage({ ok: true, text: okText });
      await load();
    } catch (err) {
      setMessage({ ok: false, text: err.message });
    } finally {
      setBusy(false);
    }
  };

  const addLab = (e) => {
    e.preventDefault();
    if (!newLab.trim()) return;
    run(() => jsonFetch(`${API}/clinic/labs`, { method: 'POST', body: JSON.stringify({ name: newLab.trim() }) }), `Laboratorio "${newLab.trim().toUpperCase()}" creado`);
    setNewLab('');
  };

  const deleteLab = (lab) => {
    if (!window.confirm(`¿Eliminar el laboratorio "${lab}" y TODOS sus productos?`)) return;
    run(() => jsonFetch(`${API}/clinic/labs/${encodeURIComponent(lab)}`, { method: 'DELETE' }), `Laboratorio "${lab}" eliminado`);
  };

  const addProduct = (lab) => (e) => {
    e.preventDefault();
    if (!newProduct.name.trim()) return;
    run(() => jsonFetch(`${API}/clinic/labs/${encodeURIComponent(lab)}/products`, { method: 'POST', body: JSON.stringify(newProduct) }), `Producto añadido a ${lab}`);
    setNewProduct({ name: '', stock_count: 0, unit_label: 'UNI' });
  };

  const deleteProduct = (id, name) => {
    if (!window.confirm(`¿Eliminar el producto "${name}"?`)) return;
    run(() => jsonFetch(`${API}/clinic/inventory/${id}`, { method: 'DELETE' }), `Producto "${name}" eliminado`);
  };

  const setDiscount = (lab) => {
    const value = window.prompt(`% de descuento para publicidad y venta de "${lab}" (0 para quitar):`, '10');
    if (value === null) return;
    run(() => jsonFetch(`${API}/clinic/labs/${encodeURIComponent(lab)}/discount`, { method: 'PATCH', body: JSON.stringify({ discount_percent: Number(value) || 0 }) }), `Descuento del ${Number(value) || 0}% aplicado a ${lab}`);
  };

  if (!data) return <div className="rounded-[28px] border border-white/60 bg-white/95 p-6 text-sm text-slate-500">Cargando laboratorios…</div>;

  return (
    <section className="rounded-[28px] border border-white/60 bg-white/95 p-6 shadow-[0_20px_60px_rgba(15,23,42,0.08)]" data-testid="clinic-labs-panel">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.24em] text-[#0f766e]">Centro San Borja</p>
          <h2 className="mt-1 text-2xl font-black text-slate-900">Productos por laboratorio</h2>
          <p className="text-sm text-slate-500">{data.total_labs} laboratorios · {data.total_products} productos</p>
        </div>
        {canManage && (
          <form onSubmit={addLab} className="flex gap-2">
            <input value={newLab} onChange={(e) => setNewLab(e.target.value)} placeholder="Nuevo laboratorio" className="rounded-full border border-slate-200 px-4 py-2 text-sm" data-testid="clinic-labs-new-input" />
            <button type="submit" disabled={busy} className="rounded-full bg-slate-900 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50" data-testid="clinic-labs-add-button">+ Añadir</button>
          </form>
        )}
      </div>

      {message && <p className={`mt-3 text-sm ${message.ok ? 'text-emerald-600' : 'text-red-500'}`} data-testid="clinic-labs-message">{message.text}</p>}

      <div className="mt-5 space-y-2 max-h-[560px] overflow-y-auto pr-1">
        {data.labs.map((lab) => (
          <div key={lab.lab} className="rounded-2xl border border-slate-200" data-testid={`clinic-lab-${lab.lab.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase()}`}>
            <button type="button" onClick={() => setOpen(open === lab.lab ? null : lab.lab)} className="flex w-full items-center justify-between px-4 py-3 text-left">
              <span className="font-bold text-slate-900 text-sm">{lab.lab}</span>
              <span className="flex items-center gap-2 text-xs text-slate-500">
                {lab.discount_percent > 0 && <span className="rounded-full bg-rose-100 px-2 py-0.5 font-bold text-rose-600">PROMO -{lab.discount_percent}%</span>}
                {lab.products.length} productos {open === lab.lab ? '▲' : '▼'}
              </span>
            </button>
            {open === lab.lab && (
              <div className="border-t border-slate-100 px-4 py-3 space-y-2">
                {canManage && (
                  <div className="flex flex-wrap gap-2 pb-2">
                    <button type="button" onClick={() => setDiscount(lab.lab)} disabled={busy} className="rounded-full bg-rose-500 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50" data-testid="clinic-lab-discount-button">% Descuento publicidad/venta</button>
                    <button type="button" onClick={() => deleteLab(lab.lab)} disabled={busy} className="rounded-full border border-red-300 px-3 py-1.5 text-xs font-bold text-red-500 disabled:opacity-50" data-testid="clinic-lab-delete-button">Eliminar laboratorio</button>
                  </div>
                )}
                {lab.products.map((p) => (
                  <div key={p.id} className="flex items-center justify-between rounded-xl bg-slate-50 px-3 py-2 text-sm">
                    <span className="text-slate-800">{p.name}
                      {p.discount_percent > 0 && <span className="ml-2 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-600">-{p.discount_percent}%</span>}
                    </span>
                    <span className="flex items-center gap-3 text-xs text-slate-500">
                      {p.stock_count} {p.unit_label}
                      {canManage && <button type="button" onClick={() => deleteProduct(p.id, p.name)} disabled={busy} className="text-red-400 hover:text-red-600 font-bold" data-testid={`clinic-product-delete-${p.id}`}>✕</button>}
                    </span>
                  </div>
                ))}
                {canManage && (
                  <form onSubmit={addProduct(lab.lab)} className="flex flex-wrap gap-2 pt-2">
                    <input value={newProduct.name} onChange={(e) => setNewProduct((prev) => ({ ...prev, name: e.target.value }))} placeholder="Nuevo producto" className="flex-1 min-w-[140px] rounded-full border border-slate-200 px-3 py-1.5 text-xs" data-testid="clinic-product-new-input" />
                    <input type="number" min="0" value={newProduct.stock_count} onChange={(e) => setNewProduct((prev) => ({ ...prev, stock_count: Number(e.target.value) }))} className="w-20 rounded-full border border-slate-200 px-3 py-1.5 text-xs" title="Stock" />
                    <button type="submit" disabled={busy} className="rounded-full bg-emerald-600 px-3 py-1.5 text-xs font-bold text-white disabled:opacity-50" data-testid="clinic-product-add-button">+ Producto</button>
                  </form>
                )}
              </div>
            )}
          </div>
        ))}
        {!data.labs.length && <p className="text-sm text-slate-500">Sin laboratorios todavía. Añade el primero arriba.</p>}
      </div>
    </section>
  );
};

export default ClinicLabsPanel;
